// tslint:disable:max-file-line-count
import { ITargetsParams } from '@cian/adfox/build/api';
import {
  calculatedParamsMiddleware,
  IAdFoxBannerTypeParams,
  IAdFoxParams,
  IAdFoxTargets,
  mergeMiddlewares,
} from '@cian/adfox/build/middlewares';
import { AdFoxBanner, IAdFoxBannerContext } from '@cian/adfox/build/react';
import { LazyLoad } from '@cian/components';
import * as React from 'react';
import { NoSsr } from '@cian/react-utils';
import { mergeStyles } from '@cian/utils/lib/shared/style';
import { EAdFoxBannerType } from '@cian/adfox-component';

import { sendStats } from '../../../api/stats';
import { IOffer } from '../../../offer/types';
import { IMakeRequest } from '../../../utils/request';
import { AdFoxBannerType, BANNER_TEMPLATE_TYPE, getBannerByType } from './templates';
import { CommercialMortgageBanner } from './templates/commercial_mortgage/';
import { GoogleDefaultBanner } from './templates/google_default';
import { HTML5Banner } from './templates/html_5';
import { MoreParametersPikBannerContainer } from './templates/more_parameters';
import { MortgageBanner } from './templates/mortgage_in_price';
import { ParallaxBanner } from './templates/parallax';
import { TGFullBanner } from './templates/tgb_in_card/full';
import { TGSimplifiedBannerContainer } from './templates/tgb_in_card/simplified/container';
import { SeoBanner } from './templates/tgb_seo_top/';
import { VideoBanner } from './templates/video';
import { MortgageSimpleBannerContainer } from './templates/mortgage';
import { MortgageInListing } from './templates/mortgage_in_listing';
import { AdFoxBanner as AdFoxBannerNew } from './new';
import { sendAdsEvent } from '../../../tracking/sendAdsEvent';

import * as styles from './index.css';

export interface IAddFoxBannerConfig {
  type: AdFoxBannerType;
  key: string | number;
  makeRequest: IMakeRequest;
  offer?: IOffer;
  locationId?: number;
  isCommercial?: boolean;
  additionalPuids?: { [key: string]: string };
  isAgent?: boolean;
  isNew?: boolean;
  realtyUserId?: number | null;
  shouldSendAdsEvent?: boolean;
}

interface IAdFoxMiddlewareConfig {
  id?: string;
  type: AdFoxBannerType;
  offer?: IOffer;
  additionalPuids?: { [key: string]: string };
}

// Зачение pr должно быть одно для всех баннеров в рамках одной загрузки страницы
const PR_RANDOM = getRandomId();

export function getBannerMiddleware({ type, offer, id, additionalPuids }: IAdFoxMiddlewareConfig) {
  return () =>
    bannerMiddleware(
      type,
      Promise.resolve().then(() => {
        let targetParams: ITargetsParams = {};

        if (type === 'video' || type === 'parallax') {
          Object.assign(targetParams, {
            pr: PR_RANDOM,
            puid20: 'abtest',
          });
        } else {
          Object.assign(targetParams, {
            pr: PR_RANDOM,
            pr1: id || getRandomId(),
          });
        }

        if (offer && offer.adfoxParams) {
          Object.assign(targetParams, offer.adfoxParams);
        }

        // tslint:disable-next-line:no-any
        return Object.assign(targetParams, additionalPuids) as any;
      }),
    );
}

export function addAdFoxBanner(config: IAddFoxBannerConfig) {
  const {
    isCommercial,
    key,
    locationId,
    makeRequest,
    offer,
    type,
    additionalPuids = { puid6: '' },
    isNew,
    isAgent,
    realtyUserId,
    shouldSendAdsEvent,
  } = config;

  const randomId = getRandomId();
  let savedBannerId: string;

  const onBannerClick = () => {
    if (savedBannerId) {
      sendAdsEvent({
        realtyUserId,
        action: 'click',
        bannerId: savedBannerId,
        bannerPlace: 'offersSerp',
      });
    }
  };

  const onBannerLoad = (context: IAdFoxBannerContext) => {
    if (!context || !shouldSendAdsEvent) {
      return;
    }

    const bannerId = context.bannerId;
    savedBannerId = bannerId;

    sendAdsEvent({
      realtyUserId,
      action: 'show',
      bannerId,
      bannerPlace: 'offersSerp',
    });
  };

  return getBanner(
    key,
    makeRequest,
    getBannerMiddleware({ type, additionalPuids, id: randomId, offer }),
    locationId,
    randomId,
    isCommercial,
    isNew,
    isAgent,
    onBannerLoad,
    onBannerClick,
  );
}

export function addPikAdfoxBanner(locationId: number, makeRequest: IMakeRequest) {
  const middleware = () =>
    bannerMiddleware(
      'more_parameters_pik',
      Promise.resolve<IAdFoxParams>({
        puid1: 'deal_type_sale',
        puid2: 'offer_flat',
        puid5: 'obl_id_' + locationId,
        // tslint:disable-next-line:no-any
      } as any),
    );

  return getBanner(1, makeRequest, middleware, locationId, getRandomId());
}

export function getRandomId() {
  return String(Math.floor(Math.random() * 4294967295) + 1);
}

function getBanner(
  key: string | number,
  makeRequest: IMakeRequest,
  middleware: () => Promise<IAdFoxParams>,
  locationId: number | undefined,
  randomId: string,
  isCommercial?: boolean,
  isNew?: boolean,
  isAgent?: boolean,
  onBannerLoad?: (context: IAdFoxBannerContext) => void,
  onBannerClick?: () => void,
): React.ReactNode {
  if (isNew) {
    return (
      <NoSsr key={key} defer>
        <LazyLoad
          offset={300}
          onLoad={middleware}
          renderLoaded={(result: IAdFoxParams) => {
            const { pr, pr1, ...rest } = result;

            return (
              <AdFoxBannerNew
                type={EAdFoxBannerType.Async}
                ownerId={202100}
                disableYan={isAgent}
                // tslint:disable-next-line:no-any
                params={rest as any}
              />
            );
          }}
          renderEmpty={() => {
            return getBannerMarkup(false, randomId, <span />, isCommercial);
          }}
        />
      </NoSsr>
    );
  }

  return (
    <NoSsr key={key} defer>
      <LazyLoad
        offset={300}
        onLoad={middleware}
        renderLoaded={(result: IAdFoxParams) => (
          <AdFoxBanner
            middleware={() => Promise.resolve(result)}
            renderFailure={error => {
              let errorType = 'template-error';

              if (error.message === 'Unpossible to load script') {
                errorType = !window._cianCanRunAds ? 'request-error' : 'adblock-error';
              }

              sendStats(makeRequest, errorType, key.toString());

              return getBannerMarkup(false, randomId, <span />, isAgent, isCommercial);
            }}
            renderFetched={(context: IAdFoxBannerContext) => {
              if (!!onBannerLoad && context['type'] !== 'nativeTGB') {
                onBannerLoad(context);
              }

              switch (context['type'] as BANNER_TEMPLATE_TYPE) {
                case 'nativeTGB':
                  return isCommercial
                    ? getBannerMarkup(true, randomId, <TGFullBanner context={context} />, isAgent, isCommercial)
                    : getBannerMarkup(
                        true,
                        randomId,
                        <TGSimplifiedBannerContainer context={context} locationId={locationId} />,
                        isAgent,
                        isCommercial,
                      );
                case 'mortgage_banner':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <MortgageSimpleBannerContainer context={context} />,
                    isAgent,
                    isCommercial,
                  );
                case 'mortgage':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <MortgageBanner isCommercial={isCommercial} context={context} />,
                    isAgent,
                    isCommercial,
                  );
                case 'commercial_mortgage':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <CommercialMortgageBanner context={context} />,
                    isAgent,
                    isCommercial,
                  );
                case 'seo':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <SeoBanner context={context} />,
                    isAgent,
                    isCommercial,
                    onBannerClick,
                  );
                case 'video':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <VideoBanner context={context} />,
                    isAgent,
                    isCommercial,
                    onBannerClick,
                  );
                case 'parallax':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <ParallaxBanner context={context} />,
                    isAgent,
                    isCommercial,
                    onBannerClick,
                  );
                case 'more_parameters':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <MoreParametersPikBannerContainer context={context} />,
                    isAgent,
                    isCommercial,
                    onBannerClick,
                  );
                case 'stretch':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <HTML5Banner context={context} />,
                    isAgent,
                    isCommercial,
                    onBannerClick,
                  );
                case 'google_default':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <GoogleDefaultBanner context={context} />,
                    isAgent,
                    isCommercial,
                    onBannerClick,
                  );
                case 'mortgage_in_listing':
                  return getBannerMarkup(
                    true,
                    randomId,
                    <MortgageInListing context={context} />,
                    isAgent,
                    isCommercial,
                  );
                default:
                  return getBannerMarkup(false, randomId, <span />, isAgent, isCommercial);
              }
            }}
          />
        )}
        renderEmpty={() => {
          return getBannerMarkup(false, randomId, <span />, isAgent, isCommercial);
        }}
      />
    </NoSsr>
  );
}

function getBannerMarkup(
  isValidBanner: boolean,
  randomId: string,
  markup: JSX.Element,
  isAgent?: boolean,
  isCommercial?: boolean,
  onBannerClick?: () => void,
) {
  return (
    <div
      {...mergeStyles(
        !isCommercial && styles['simplified-banner'],
        !isCommercial && !isValidBanner && styles['no-banner'],
      )}
      key={randomId}
      onClick={onBannerClick}
    >
      <div>{markup}</div>
      <div id={`AdFox_banner_${randomId}`} />
    </div>
  );
}

function bannerMiddleware(bannerType: AdFoxBannerType, request: Promise<IAdFoxTargets>): Promise<IAdFoxParams> {
  return mergeMiddlewares(request, typeParamsMiddleware(bannerType), calculatedParamsMiddleware());
}

function typeParamsMiddleware(type: AdFoxBannerType): Promise<IAdFoxBannerTypeParams> {
  return Promise.resolve(getBannerByType(type));
}
